import React, { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  getDocs,
  updateDoc,
  where,
  query,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { firestore } from "./firebase";

const auth = getAuth();
const db = firestore;

function normalizeMenus(menuListLang = {}) {
  const result = {};

  for (const [lang, menus] of Object.entries(menuListLang)) {
    result[lang] = menus.map((menu) => ({
      category: menu.category,
      items: menu.menuItems.map((name, i) => ({
        name,
        description: menu.descriptions?.[i] ?? "",
        alergener: menu.alergener?.[i] ?? "",
        price: menu.prices?.[i] ?? null,
        variants: menu.variants?.[i] ?? [],
      })),
    }));
  }

  return result;
}

const MenuReview = ({ allLocations }) => {
  const [openLocation, setOpenLocation] = useState(null);

  return (
    <div style={{ marginTop: 24 }}>
      {allLocations.map((loc) => (
        <LocationAccordion
          key={loc.id}
          location={loc}
          open={openLocation === loc.id}
          onToggle={() =>
            setOpenLocation(openLocation === loc.id ? null : loc.id)
          }
        />
      ))}
    </div>
  );
};

const LocationAccordion = ({ location, open, onToggle }) => {
  const oldMenusByLang = normalizeMenus(location.menuListLang);
  const newMenusByLang = normalizeMenus(location.newMenulistLang);

  const languages = Array.from(
    new Set([...Object.keys(oldMenusByLang), ...Object.keys(newMenusByLang)]),
  );

  const [activeLang, setActiveLang] = useState(languages[0]);

  if (!languages.length) return null;

  return (
    <div style={{ border: "1px solid #ddd", marginBottom: 12 }}>
      <div
        onClick={onToggle}
        style={{
          cursor: "pointer",
          padding: 12,
          fontWeight: "bold",
          background: "#f5f5f5",
        }}>
        {location.name}
      </div>

      {open && (
        <div style={{ padding: 12 }}>
          <LanguageTabs
            languages={languages}
            active={activeLang}
            onChange={setActiveLang}
          />

          <CategoryList
            oldMenus={oldMenusByLang[activeLang] || []}
            newMenus={newMenusByLang[activeLang] || []}
          />
        </div>
      )}
    </div>
  );
};

const LanguageTabs = ({ languages, active, onChange }) => (
  <div style={{ marginBottom: 16 }}>
    {languages.map((lang) => (
      <button
        key={lang}
        onClick={() => onChange(lang)}
        style={{
          marginRight: 8,
          fontWeight: active === lang ? "bold" : "normal",
        }}>
        {lang}
      </button>
    ))}
  </div>
);

const CategoryList = ({ oldMenus, newMenus }) => {
  const allCategories = Array.from(
    new Set([
      ...oldMenus.map((m) => m.category),
      ...newMenus.map((m) => m.category),
    ]),
  );

  const CategoryAccordion = ({ category, oldMenu, newMenu }) => {
    const [open, setOpen] = useState(false);

    return (
      <div style={{ marginBottom: 8 }}>
        <div
          onClick={() => setOpen(!open)}
          style={{
            cursor: "pointer",
            padding: 8,
            background: "#eee",
          }}>
          {category}
        </div>

        {open && (
          <div style={{ display: "flex", gap: 16, padding: 8 }}>
            <MenuColumn title='Existing' menu={oldMenu} />
            <MenuColumn title='New' menu={newMenu} />
          </div>
        )}
      </div>
    );
  };

  const MenuColumn = ({ title, menu }) => (
    <div style={{ flex: 1 }}>
      <h4>{title}</h4>
      {!menu ? (
        <em>No data</em>
      ) : (
        menu.items.map((item, i) => (
          <div
            key={i}
            style={{
              borderBottom: "1px solid #ddd",
              marginBottom: 6,
              paddingBottom: 6,
            }}>
            <strong>{item.name}</strong>
            {item.description && <div>{item.description}</div>}
            {item.price && <div>Pris: {item.price}</div>}
            {item.alergener && <div>Alergener: {item.alergener}</div>}
          </div>
        ))
      )}
    </div>
  );

  return (
    <>
      {allCategories.map((cat) => (
        <CategoryAccordion
          key={cat}
          category={cat}
          oldMenu={oldMenus.find((m) => m.category === cat)}
          newMenu={newMenus.find((m) => m.category === cat)}
        />
      ))}
    </>
  );
};

const Test = (props) => {
  const [userInformation, setUserInfomation] = useState(null);
  const [allLocations, setAllLocations] = useState([]);
  const [onlyNorwegian, setOnlyNorwegian] = useState(false);

  useEffect(() => {
    auth.onAuthStateChanged(async function (user) {
      if (user) {
        const userDoc = doc(db, "users", user.uid);

        const userInfo = await getDoc(userDoc);
        setUserInfomation(userInfo.data());
      }
    });
  }, [auth]);

  function isEmpty(obj) {
    return Object.keys(obj).length === 0;
  }

  const onGetData = async () => {
    const q = query(
      collection(db, "lokasjoner"),
      where("type", "==", "restaurant"),
      where("menuFetchUrl", "!=", null),
      where("menuFetchAvailable", "==", true),
    );

    const menusSnapshot = await getDocs(q);

    let typeURL = [];
    let typeSelectors = [];
    let typeWolt = [];
    let typePDF = [];
    let typeImage = [];
    let anyOtherType = [];
    let allTypes = [];

    menusSnapshot.forEach((menuDoc) => {
      const menuData = menuDoc.data();

      const url =
        typeof menuData.menuFetchUrl === "string"
          ? menuData.menuFetchUrl.trim()
          : "";

      // Filter out empty string (and any weird non-string values)
      if (!url) return;

      allTypes.push({
        id: menuDoc.id,
        name: menuData.locationName,
        url: url,
        menuListLang: menuData.menuListLang || {},
        newMenulistLang: {},
        menuFetchUrlType: menuData.menuFetchUrlType,
        lang: menuData.lang,
        ignoreTerms: menuData.ignoreTerms || "",
        includeTerms: menuData.includeTerms || "",
        selectors: menuData.menuFetchSelectors || {},
        menuHash: menuData.menuHash || null,
        newMenuHash: null,
        apiMapping: menuData.apiMapping ? menuData.apiMapping : null,
        multipleApiMappingidObjects: menuData.multipleApiMappingidObjects
          ? menuData.multipleApiMappingidObjects
          : null,
        multipleApiURL: menuData.multipleApiURL
          ? menuData.multipleApiURL
          : null,
      });

      if (menuData.menuFetchUrlType === "URL") {
        typeURL.push({
          id: menuDoc.id,
          name: menuData.locationName,
          url: url,
          menuListLang: menuData.menuListLang || {},
          newMenulistLang: {},
        });
      } else if (
        menuData.menuFetchSelectors &&
        !isEmpty(menuData.menuFetchSelectors) &&
        menuData.menuFetchSelectors.category_name !== ""
      ) {
        typeSelectors.push({
          id: menuDoc.id,
          name: menuData.locationName,
          url: url,
          selectors: menuData.menuFetchSelectors,
          menuListLang: menuData.menuListLang || {},
          newMenulistLang: {},
        });
      } else if (menuData.menuFetchUrlType === "wolt") {
        typeWolt.push({
          id: menuDoc.id,
          name: menuData.locationName,
          url: url,
          menuListLang: menuData.menuListLang || {},
          newMenulistLang: {},
        });
      } else if (menuData.menuFetchUrlType === "images") {
        typePDF.push({
          id: menuDoc.id,
          name: menuData.locationName,
          url: url,
          menuListLang: menuData.menuListLang || {},
          newMenulistLang: {},
        });
      } else if (menuData.menuFetchUrlType === "PDFs") {
        typeImage.push({
          id: menuDoc.id,
          name: menuData.locationName,
          url: url,
          menuListLang: menuData.menuListLang || {},
          newMenulistLang: {},
        });
      } else {
        anyOtherType.push({
          id: menuDoc.id,
          name: menuData.locationName,
          url: url,
          menuListLang: menuData.menuListLang || {},
          newMenulistLang: {},
        });
      }

      console.log(
        "Fetching menu for:",
        menuData.locationName,
        url,
        menuDoc.id,
        menuData.menuFetchUrlType,
      );
    });
    console.log(typeURL.length, "with URL");
    console.log(typeSelectors.length, "with Selectors");
    console.log(typeWolt.length, "with Wolt");
    console.log(typePDF.length, "with PDF");
    console.log(typeImage.length, "with Image");
    console.log(anyOtherType.length, "with Other");
    setAllLocations(allTypes);
  };

  async function getMenuFromURL(url) {
    try {
      const response = await fetch(
        "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/scrape-menutext-from-web",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url }),
        },
      );

      const data = await response.json();

      return data.menuData;
    } catch (err) {
      console.error("Request error:", err);
    }
  }

  async function fetchMenuWithSelectors({ url, selectors }) {
    console.log("Fetching menu with selectors for URL:", url, selectors);
    const fullConfig = {
      url,
      selectors,
    };

    const language = "no"; // or "no", "sv", "fi", etc. — dynamic per country

    const response = await fetch(
      "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/getMenu",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ config: fullConfig, language }),
      },
    );

    const data = await response.json();
    console.log("Menu fetch with selectors data:", data);
    return data.menuData;
  }

  async function getMenuFromWolt(url) {
    try {
      const response = await fetch(
        "https://wolt-scraper-609436138667.europe-north1.run.app/scrape",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url }),
        },
      );

      if (!response.ok) {
        console.error(
          "Server responded with:",
          response.status,
          response.statusText,
        );
        return;
      }

      const data = await response.json();
      return data.categories;
    } catch (err) {
      console.error("Request error:", err);
    }
  }

  async function getPdfsFromWebsite({ url, ignoreTerms, includeTerms }) {
    try {
      // ✅ Convert comma-separated strings → clean arrays
      const ignoreTermsArray = ignoreTerms
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      const includeTermsArray = includeTerms
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      const response = await fetch(
        "https://pdf-scraper-609436138667.europe-west1.run.app/scrape-pdf",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            url: url,
            ignoreTerms: ignoreTermsArray,
            onlyTheseTerms: includeTermsArray,
          }),
        },
      );

      const data = await response.json();
      console.log("PDF scrape data:", data);

      if (!data.success || !Array.isArray(data.images)) {
        throw new Error(data.error || "PDF scrape failed");
      }

      const rawResults = await Promise.all(
        data.images.map((image) =>
          getMenuFromImage({
            imageUrl: image.imageUrl,
          }),
        ),
      );

      const cleanedMenuData = rawResults.filter(Boolean).flat();

      return cleanedMenuData;
    } catch (err) {
      console.error("Request error:", err);
    }
  }

  async function getImagesFromWebsite(url) {
    try {
      const response = await fetch(
        "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/scrape-images",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ url }),
        },
      );

      const data = await response.json();

      console.log("Image scrape data:", data);

      const rawResults = await Promise.all(
        data.images.map((imageUrl) => getMenuFromImage({ imageUrl: imageUrl })),
      );

      // ✅ CLEANUP STEP
      const cleanedMenuData = rawResults
        .filter(Boolean) // remove null / undefined
        .flat(); // flatten arrays into 1 level

      console.log("✅ Cleaned menu:", cleanedMenuData);
      return cleanedMenuData;
    } catch (err) {
      console.error("Request error:", err);
    }
  }

  async function getMenuFromImage({ imageUrl, base64 }) {
    try {
      const body = imageUrl !== null ? { imageUrl } : { imageBase64: base64 };

      const response = await fetch(
        "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/getMenuFromImage",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );

      const data = await response.json();

      if (!data?.menuData?.length) return null;
      return data.menuData;
    } catch (err) {
      console.error("Request error:", err);
      return null;
    }
  }

  const fetchMenuFromAPI = async ({
    menuFetchUrl,
    apiMapping,
    multipleApiMappingidObjects,
    multipleApiURL,
  }) => {
    if (!menuFetchUrl || !apiMapping) {
      throw new Error("Missing menuFetchUrl or apiMapping");
    }

    // ✅ MULTI-API MODE
    if (multipleApiURL) {
      try {
        const response = await fetch(
          "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/getMenuApiTest",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ apiUrl: multipleApiURL }),
          },
        );

        const ids = await response.json();

        if (!Array.isArray(ids)) {
          throw new Error("Expected array of IDs");
        }

        if (!multipleApiMappingidObjects) {
          throw new Error("Missing multipleApiMappingidObjects");
        }

        const menus = await Promise.all(
          ids.map(async (idObj) => {
            const id = idObj[multipleApiMappingidObjects];
            if (!id) return [];

            const url = menuFetchUrl + id;

            const res = await fetch(
              "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/getMenuFromAPI",
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  apiUrl: url,
                  mapping: apiMapping,
                }),
              },
            );

            return await res.json();
          }),
        );

        console.log("Multi-API fetch menus:", menus);

        return menus.flat();
      } catch (err) {
        console.error("Multi-API fetch error:", err);
        return [];
      }
    }

    // ✅ SINGLE-API MODE
    try {
      const response = await fetch(
        "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/getMenuFromAPI",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            apiUrl: menuFetchUrl,
            mapping: apiMapping,
          }),
        },
      );

      return await response.json();
    } catch (err) {
      console.error("Single-API fetch error:", err);
      return [];
    }
  };

  async function runWithConcurrency(items, limit, handler) {
    const results = [];
    let index = 0;

    async function worker() {
      while (index < items.length) {
        const currentIndex = index++;
        try {
          results[currentIndex] = await handler(items[currentIndex]);
        } catch (err) {
          console.error("Worker error:", err);
          results[currentIndex] = items[currentIndex];
        }
      }
    }

    const workers = Array.from({ length: limit }, worker);
    await Promise.all(workers);
    return results;
  }

  function groupLocationsByMenuHash(locations) {
    const groups = new Map();

    for (const loc of locations) {
      const key = loc.menuHash || `__unique__${loc.id}`;

      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key).push(loc);
    }

    return groups;
  }

  // const onGetNewMenus = async () => {
  //   console.log("onGetNewMenus called, allLocations:", allLocations.length);

  //   // 1️⃣ Split locations by scraper type
  //   const woltLocations = [];
  //   const otherLocations = [];

  //   for (const location of allLocations) {
  //     if (location.menuFetchUrlType === "wolt") {
  //       woltLocations.push(location);
  //     } else {
  //       otherLocations.push(location);
  //     }
  //   }

  //   // 2️⃣ Run NON-WOLT scrapers in parallel (safe)
  //   const otherResults = await Promise.all(
  //     otherLocations.map(async (location) => {
  //       let menuData = null;

  //       try {
  //         if (location.menuFetchUrlType === "URL") {
  //           menuData = await getMenuFromURL(location.url);
  //         } else if (
  //           location.selectors &&
  //           !isEmpty(location.selectors) &&
  //           location.selectors.category_name?.trim() !== ""
  //         ) {
  //           menuData = await fetchMenuWithSelectors({
  //             url: location.url,
  //             selectors: location.selectors,
  //           });
  //         } else if (location.menuFetchUrlType === "PDFs") {
  //           menuData = await getPdfsFromWebsite({
  //             url: location.url,
  //             ignoreTerms: location.ignoreTerms,
  //             includeTerms: location.includeTerms,
  //           });
  //         } else if (location.menuFetchUrlType === "images") {
  //           menuData = await getImagesFromWebsite(location.url);
  //         }
  //       } catch (err) {
  //         console.error("Menu fetch failed:", location.name, err);
  //       }

  //       if (!menuData) return location;

  //       return {
  //         ...location,
  //         newMenulistLang: {
  //           ...(location.newMenulistLang || {}),
  //           [location.lang]: menuData,
  //         },
  //       };
  //     })
  //   );

  //   const WOLT_CONCURRENCY = 5; // 🔥 safe value

  //   const woltResults = await runWithConcurrency(
  //     woltLocations,
  //     WOLT_CONCURRENCY,
  //     async (location) => {
  //       try {
  //         console.log("Fetching Wolt menu:", location.name);
  //         const menuData = await getMenuFromWolt(location.url);

  //         if (!menuData) return location;

  //         return {
  //           ...location,
  //           newMenulistLang: {
  //             ...(location.newMenulistLang || {}),
  //             [location.lang]: menuData,
  //           },
  //         };
  //       } catch (err) {
  //         console.error("Wolt fetch failed:", location.name, err);
  //         return location;
  //       }
  //     }
  //   );

  //   // 4️⃣ Merge back in original order
  //   const updatedMap = new Map(
  //     [...otherResults, ...woltResults].map((l) => [l.id, l])
  //   );

  //   const finalLocations = allLocations.map(
  //     (loc) => updatedMap.get(loc.id) || loc
  //   );

  //   setAllLocations(finalLocations);
  // };

  function groupByMenuHash(locations) {
    const map = new Map();

    for (const loc of locations) {
      const key = loc.menuHash ?? `__unique__${loc.id}`;

      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(loc);
    }

    return map;
  }

  const onGetNewMenus = async () => {
    console.log("onGetNewMenus called, allLocations:", allLocations.length);

    // 🔹 Group locations by menuHash (deduplication)
    const groupByMenuHash = (locations) => {
      const map = new Map();
      for (const loc of locations) {
        const key = loc.menuHash ?? `__unique__${loc.id}`;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(loc);
      }
      return map;
    };

    const groups = groupByMenuHash(allLocations);

    // 🔹 Pick one representative per group
    const representatives = [];
    const groupMap = new Map(); // rep.id -> full group

    for (const group of groups.values()) {
      const leader = group[0];
      representatives.push(leader);
      groupMap.set(leader.id, group);
    }

    // 1️⃣ Split representatives by scraper type
    const woltLocations = [];
    const otherLocations = [];

    for (const location of representatives) {
      if (location.menuFetchUrlType === "wolt") {
        woltLocations.push(location);
      } else {
        otherLocations.push(location);
      }
    }

    // 2️⃣ Run NON-WOLT scrapers in parallel
    const otherResults = await Promise.all(
      otherLocations.map(async (location) => {
        let menuData = null;

        try {
          if (location.menuFetchUrlType === "URL") {
            menuData = await getMenuFromURL(location.url);
          } else if (
            location.menuFetchUrlType === "api" ||
            location.menuFetchUrlType === "multipleApi"
          ) {
            menuData = await fetchMenuFromAPI({
              menuFetchUrl: location.url,
              apiMapping: location.apiMapping,
              multipleApiMappingidObjects: location.multipleApiMappingidObjects,
              multipleApiURL: location.multipleApiURL,
            });
          } else if (location.menuFetchUrlType === "PDFs") {
            menuData = await getPdfsFromWebsite({
              url: location.url,
              ignoreTerms: location.ignoreTerms,
              includeTerms: location.includeTerms,
            });
          } else if (location.menuFetchUrlType === "images") {
            menuData = await getImagesFromWebsite(location.url);
          } else if (
            location.selectors &&
            !isEmpty(location.selectors) &&
            location.selectors.category_name?.trim() !== ""
          ) {
            menuData = await fetchMenuWithSelectors({
              url: location.url,
              selectors: location.selectors,
            });
          }
        } catch (err) {
          console.error("Menu fetch failed:", location.name, err);
        }

        const group = groupMap.get(location.id) || [location];

        if (!menuData) return group;

        const newMenuHash = await createMenuHash(menuData);

        return group.map((loc) => ({
          ...loc,
          newMenuHash,
          newMenulistLang: {
            ...(loc.newMenulistLang || {}),
            [loc.lang]: menuData,
          },
        }));
      }),
    );

    // 3️⃣ Run WOLT scrapers with concurrency limit
    const WOLT_CONCURRENCY = 5;

    const woltResults = await runWithConcurrency(
      woltLocations,
      WOLT_CONCURRENCY,
      async (location) => {
        const group = groupMap.get(location.id) || [location];

        try {
          console.log("Fetching Wolt menu:", location.name);
          const menuData = await getMenuFromWolt(location.url);

          if (!menuData) return group;

          const newMenuHash = await createMenuHash(menuData);

          return group.map((loc) => ({
            ...loc,
            newMenuHash,
            newMenulistLang: {
              ...(loc.newMenulistLang || {}),
              [loc.lang]: menuData,
            },
          }));
        } catch (err) {
          console.error("Wolt fetch failed:", location.name, err);
          return group;
        }
      },
    );

    // 4️⃣ Merge back in original order
    const updatedMap = new Map(
      [...otherResults.flat(), ...woltResults.flat()].map((l) => [l.id, l]),
    );

    const finalLocations = allLocations.map(
      (loc) => updatedMap.get(loc.id) || loc,
    );

    setAllLocations(finalLocations);
    console.log("Menu fetching completed");
  };

  const updateLocationMenuInDb = async () => {
    const empty = [];
    const nonEmpty = [];
    const menusChanged = Date.now();

    for (const location of allLocations) {
      const lang = location.lang;
      const menu = location.newMenulistLang?.[lang];
      const menuEng = location.newMenulistLang?.en_US;

      const isEmptyMenu =
        !lang || !menu || !Array.isArray(menu) || menu.length === 0;

      if (isEmptyMenu) {
        empty.push(location.name);
        continue;
      }

      nonEmpty.push(location.name);

      const locationRef = doc(db, "lokasjoner", location.id);

      const updatePayload = {
        menusChanged,
        [`menuListLang.${lang}`]: menu,
        menuHash: location.newMenuHash,
        menus: menu,
      };

      // ✅ Only write English menu if it exists
      if (Array.isArray(menuEng) && menuEng.length > 0) {
        updatePayload["menuListLang.en_US"] = menuEng;
      }

      await updateDoc(locationRef, updatePayload);
    }

    console.log("Locations WITH new menus:", nonEmpty, nonEmpty.length);
    console.log("Locations WITHOUT new menus:", empty, empty.length);
  };

  const TARGET_LANG = "en-US";

  const translateBatch = async ({ texts, lang }) => {
    try {
      const res = await fetch(
        "https://us-central1-menuapp-4c413.cloudfunctions.net/tripadvisor/translateWithDeepL",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            texts,
            toLang: TARGET_LANG,
            fromLang: lang,
          }),
        },
      );

      const json = await res.json();
      return json.text ?? texts;
    } catch (err) {
      console.error("Batch translate error:", err);
      return texts;
    }
  };

  const handleTranslate = async () => {
    console.log("Starting translation to en_US");

    const groups = groupByNewMenuHash(allLocations);
    const translatedByHash = new Map();

    // 1️⃣ Translate each UNIQUE menu once
    for (const [hash, locations] of groups.entries()) {
      const leader = locations[0];

      // 🔒 Skip if menu did not change
      if (leader.menuHash && leader.menuHash === leader.newMenuHash) {
        console.log("Skipping unchanged menu:", leader.name);
        continue;
      }

      const sourceLang = leader.lang;
      const menuList = leader.newMenulistLang?.[sourceLang];

      if (!menuList || !Array.isArray(menuList)) continue;

      console.log("Translating menu hash:", hash);

      const categories = menuList.map((c) => c.category);
      const items = menuList.flatMap((c) => c.menuItems);
      const alergener = menuList.flatMap((c) => c.alergener);
      const descriptions = menuList.flatMap((c) => c.descriptions);

      const [
        translatedCategories,
        translatedItems,
        translatedAlergener,
        translatedDescriptions,
      ] = await Promise.all([
        translateBatch({ texts: categories, lang: sourceLang }),
        translateBatch({ texts: items, lang: sourceLang }),
        translateBatch({ texts: alergener, lang: sourceLang }),
        translateBatch({ texts: descriptions, lang: sourceLang }),
      ]);

      let itemIndex = 0;
      let descIndex = 0;
      let algIndex = 0;

      const translatedMenu = menuList.map((c, i) => {
        const menuItems = Array.isArray(c.menuItems) ? c.menuItems : [];
        const alergener = Array.isArray(c.alergener)
          ? c.alergener
          : menuItems.map(() => "");
        const descriptions = Array.isArray(c.descriptions)
          ? c.descriptions
          : menuItems.map(() => "");

        return {
          category: translatedCategories[i] ?? c.category,
          menuItems: menuItems.map(() => translatedItems[itemIndex++] ?? ""),
          alergener: alergener.map(() => translatedAlergener[algIndex++] ?? ""),
          descriptions: descriptions.map(
            () => translatedDescriptions[descIndex++] ?? "",
          ),
          prices: Array.isArray(c.prices) ? c.prices : [],
          variants: c.variants ?? {},
        };
      });

      translatedByHash.set(hash, translatedMenu);
    }

    // 2️⃣ Apply translated menus to ALL locations
    const updatedLocations = allLocations.map((loc) => {
      const key = loc.newMenuHash ?? `__unique__${loc.id}`;
      const translatedMenu = translatedByHash.get(key);

      if (!translatedMenu) return loc;

      return {
        ...loc,
        newMenulistLang: {
          ...(loc.newMenulistLang || {}),
          en_US: translatedMenu,
        },
      };
    });

    setAllLocations(updatedLocations);

    console.log("Translation completed");
  };

  function groupByNewMenuHash(locations) {
    const map = new Map();

    for (const loc of locations) {
      const key = loc.newMenuHash ?? `__unique__${loc.id}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(loc);
    }

    return map;
  }

  // STABLE STRINGIFY + HASHING
  function normalize(value) {
    if (Array.isArray(value)) {
      // IMPORTANT: preserve order
      return value.map(normalize);
    }

    if (value && typeof value === "object") {
      return Object.keys(value)
        .sort()
        .reduce((acc, key) => {
          acc[key] = normalize(value[key]);
          return acc;
        }, {});
    }

    return value;
  }

  function stableStringify(obj) {
    return JSON.stringify(normalize(obj));
  }

  async function createMenuHash(menu) {
    const normalized = stableStringify(menu);

    const encoder = new TextEncoder();
    const data = encoder.encode(normalized);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);

    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }

  const updateAllReview = async () => {
    const q = query(collection(db, "lokasjoner"));
    const locationSnapshot = await getDocs(q);

    const updatePromises = locationSnapshot.docs.map(async (locationDoc) => {
      try {
        const location = locationDoc.data();

        const yelpRating = location.yelpRating || 0;
        const tripAdvisorRating = location.tripadvisorRating || 0;
        const googleRating = location.googleRating || 0;

        let x = 0;
        let count = 0;

        if (yelpRating > 0) {
          x += yelpRating;
          count++;
        }

        if (tripAdvisorRating > 0) {
          x += tripAdvisorRating;
          count++;
        }

        if (googleRating > 0) {
          x += googleRating;
          count++;
        }

        if (count > 0) {
          x = x / count;
        } else {
          x = 0;
        }

        // Clamp
        x = Math.min(5, x);

        // Round to 1 decimal
        x = Math.round(x * 10) / 10;

        const locationRef = doc(db, "lokasjoner", locationDoc.id);
        const listRef = doc(db, "lokasjoner_list", locationDoc.id);

        // Update main doc
        await updateDoc(locationRef, {
          overAllRating: x,
        });

        // Update list doc ONLY if it exists
        const listSnap = await getDoc(listRef);

        if (listSnap.exists()) {
          await updateDoc(listRef, {
            overAllRating: x,
          });
        } else {
          console.log(
            `⚠️ lokasjoner_list missing for ${locationDoc.id}, skipping`,
          );
        }

        console.log(`✅ Updated ${locationDoc.id} → ${x}`);
      } catch (err) {
        console.error(`❌ Failed updating ${locationDoc.id}`, err);
      }
    });

    await Promise.all(updatePromises);

    console.log("🎉 All locations processed");
  };

  const filterLocations = allLocations.filter((loc) =>
    loc.lang.includes("nb_NO"),
  );

  const dataURLToBlob = async (dataURL) => {
    const response = await fetch(dataURL);
    return await response.blob();
  };

  const migrateLocationMapImages = async () => {
    try {
      const storage = getStorage();
      const locationsRef = collection(db, "lokasjoner");
      const snapshot = await getDocs(locationsRef);

      console.log(`Found ${snapshot.size} locations`);

      let migratedCount = 0;
      let skippedCount = 0;
      let errorCount = 0;

      for (const locationDoc of snapshot.docs) {
        try {
          const data = locationDoc.data();
          const mapImage = data?.mapImage;

          if (!mapImage || typeof mapImage !== "string") {
            console.log(`Skipping ${locationDoc.id}: no mapImage`);
            skippedCount++;
            continue;
          }

          // Skip if already a normal URL
          if (
            mapImage.startsWith("http://") ||
            mapImage.startsWith("https://")
          ) {
            console.log(`Skipping ${locationDoc.id}: already migrated`);
            skippedCount++;
            continue;
          }

          // Only migrate old base64/data URLs
          if (!mapImage.startsWith("data:image")) {
            console.log(
              `Skipping ${locationDoc.id}: mapImage is neither URL nor data:image`,
            );
            skippedCount++;
            continue;
          }

          console.log(`Migrating ${locationDoc.id}...`);

          const blob = await dataURLToBlob(mapImage);

          // Try to preserve file extension from mime type
          const contentType = blob.type || "image/png";
          let extension = "png";

          if (contentType.includes("jpeg")) extension = "jpg";
          if (contentType.includes("webp")) extension = "webp";

          const storageRef = ref(
            storage,
            `locationMapImages/${locationDoc.id}.${extension}`,
          );

          await uploadBytes(storageRef, blob, {
            contentType,
          });

          const downloadURL = await getDownloadURL(storageRef);

          await updateDoc(doc(db, "lokasjoner", locationDoc.id), {
            mapImage: downloadURL,
          });

          console.log(`Migrated ${locationDoc.id}`);
          migratedCount++;
        } catch (error) {
          console.error(`Error migrating ${locationDoc.id}:`, error);
          errorCount++;
        }
      }

      console.log("Migration finished");
      console.log("Migrated:", migratedCount);
      console.log("Skipped:", skippedCount);
      console.log("Errors:", errorCount);
    } catch (error) {
      console.error("Migration failed:", error);
    }
  };

  return (
    userInformation &&
    userInformation.admin && (
      <>
        <button onClick={() => onGetData()}>Run Get Old Menus</button>
        <button onClick={() => onGetNewMenus()}>Run Get New Menus</button>
        <button onClick={() => handleTranslate()}>Translate All Data</button>
        <button onClick={() => updateLocationMenuInDb()}>
          Update Location Menus in DB
        </button>
        <button onClick={() => updateAllReview()}>Update All Reviews</button>
        <button onClick={() => setOnlyNorwegian(!onlyNorwegian)}>
          {onlyNorwegian
            ? "Show All Locations"
            : "Show Only Norwegian Locations"}
        </button>
        <button onClick={() => migrateLocationMapImages()}>
          Migrate Location Map Images
        </button>
        {allLocations.length > 0 && (
          <MenuReview
            allLocations={onlyNorwegian ? filterLocations : allLocations}
          />
        )}
      </>
    )
  );
};

export default Test;
